/* Creative Sukkah — 3D sukkah preview.

   Shows the customer's mural on the real walls of a sukkah at true size, so
   the numbers they typed stop being abstract. One scene unit is one inch.

   Walls are taken in the order they were entered and wrapped around the room:
   wall 1 is the back wall, wall 2 the right, wall 3 the front, wall 4 the left.
   Each banner shows its own slice of the one continuous picture, so the scene
   carries on around the corners exactly as it will when printed.

   The viewer boots only when the shopper opens it — three.js is fetched on
   demand — and it follows the customizer through the "sign:change" event
   rather than reaching into the customizer's internals. */
(function () {
  'use strict';

  var root = document.querySelector('[data-sukkah-3d]');
  if (!root) return;

  var canvasHost = root.querySelector('[data-sukkah-canvas]');
  var openButton = root.querySelector('[data-sukkah-open]');
  var panel = root.querySelector('[data-sukkah-panel]');
  var statusNote = root.querySelector('[data-sukkah-status]');
  var wallSelect = root.querySelector('[data-sukkah-wall-size]');
  var resetButton = root.querySelector('[data-sukkah-reset]');

  var THREEJS_URL = root.getAttribute('data-three-url');
  var TEXTURES = {
    wall: root.getAttribute('data-wall-texture'),
    roof: root.getAttribute('data-roof-texture'),
    floor: root.getAttribute('data-floor-texture')
  };

  var state = {
    walls: [{ width: 96, height: 48, left: 0 }],
    muralWidth: 96,
    muralHeight: 48,
    cropX: 0.5,
    cropY: 0.5,
    mode: 'horizontal',
    imageUrl: root.getAttribute('data-image-url') || '',
    fallbackWall: 0
  };

  var viewer = null;
  var booting = false;

  function setStatus(message, isError) {
    if (!statusNote) return;
    statusNote.textContent = message || '';
    statusNote.hidden = !message;
    statusNote.classList.toggle('is-error', !!isError);
  }

  function loadThree() {
    if (window.THREE) return Promise.resolve(window.THREE);
    if (!THREEJS_URL) return Promise.reject(new Error('missing three.js url'));
    return new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = THREEJS_URL;
      script.onload = function () {
        if (window.THREE) resolve(window.THREE);
        else reject(new Error('three.js did not load'));
      };
      script.onerror = function () { reject(new Error('three.js did not load')); };
      document.head.appendChild(script);
    });
  }

  function open() {
    if (panel) panel.hidden = false;
    root.classList.add('is-open');
    if (openButton) {
      openButton.setAttribute('aria-expanded', 'true');
      openButton.textContent = 'Hide the sukkah view';
    }
    if (viewer) { viewer.resize(); viewer.apply(state); return; }
    if (booting) return;
    booting = true;
    setStatus('Building your sukkah…');
    loadThree().then(function (THREE) {
      viewer = createViewer(THREE);
      booting = false;
      setStatus('');
      viewer.apply(state);
    }).catch(function () {
      booting = false;
      setStatus('The 3D view could not load. Your mural, sizes and price are unaffected.', true);
    });
  }

  function close() {
    if (panel) panel.hidden = true;
    root.classList.remove('is-open');
    if (openButton) {
      openButton.setAttribute('aria-expanded', 'false');
      openButton.textContent = 'See how it looks in a real sukkah';
    }
  }

  if (openButton) {
    openButton.addEventListener('click', function () {
      if (root.classList.contains('is-open')) close();
      else open();
    });
  }

  /* ====================================================================== */

  function createViewer(THREE) {
    var scene = new THREE.Scene();
    scene.background = new THREE.Color(0xe9ddcd);

    var camera = new THREE.PerspectiveCamera(45, 1, 1, 8000);
    var renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    canvasHost.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0xfff3e2, 0x6f5b45, 1.1));
    var sun = new THREE.DirectionalLight(0xfff0d8, 0.5);
    sun.position.set(80, 300, 160);
    scene.add(sun);

    var loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    var maxAnisotropy = (renderer.capabilities && renderer.capabilities.getMaxAnisotropy)
      ? renderer.capabilities.getMaxAnisotropy() : 1;

    function tiled(url, repeatX, repeatY) {
      /* Textures arrive asynchronously and render black until they do. The
         scene only draws when something asks it to, so a texture that finishes
         loading after the last draw would leave a black wall, roof or floor on
         screen for ever. Redraw when each one lands. */
      var texture = loader.load(url, invalidate, undefined, invalidate);
      texture.wrapS = THREE.MirroredRepeatWrapping;
      texture.wrapT = THREE.MirroredRepeatWrapping;
      texture.repeat.set(repeatX, repeatY);
      texture.anisotropy = maxAnisotropy;
      return texture;
    }

    /* A 2×4 on edge, in inches. Used for the corner posts, the top plate and
       the cross beams, and it is also the gap the banners leave at each
       corner — the strip of pole a real sukkah wall never covers. */
    var POST = 3.5;

    var room = new THREE.Group();
    scene.add(room);
    var signs = new THREE.Group();
    scene.add(signs);

    var roomParts = [];
    function clearRoom() {
      roomParts.forEach(function (part) {
        room.remove(part);
        if (part.geometry) part.geometry.dispose();
        if (part.material) {
          if (part.material.map) part.material.map.dispose();
          part.material.dispose();
        }
      });
      roomParts = [];
    }

    function addPart(mesh) {
      room.add(mesh);
      roomParts.push(mesh);
      return mesh;
    }

    function buildRoom(width, depth, height) {
      clearRoom();

      addPart(new THREE.Mesh(
        new THREE.PlaneGeometry(width, depth),
        new THREE.MeshLambertMaterial({ map: tiled(TEXTURES.floor, width / 60, depth / 60), color: 0xd6cec2 })
      )).rotation.x = -Math.PI / 2;

      /* One plank tile ≈ 40 inches, so the wood stays life-sized at any size. */
      var backMat = new THREE.MeshLambertMaterial({ map: tiled(TEXTURES.wall, width / 40, height / 60) });
      var sideMat = new THREE.MeshLambertMaterial({ map: tiled(TEXTURES.wall, depth / 40, height / 60) });

      var back = addPart(new THREE.Mesh(new THREE.PlaneGeometry(width, height), backMat));
      back.position.set(0, height / 2, -depth / 2);

      var front = addPart(new THREE.Mesh(new THREE.PlaneGeometry(width, height), backMat));
      front.position.set(0, height / 2, depth / 2);
      front.rotation.y = Math.PI;

      var left = addPart(new THREE.Mesh(new THREE.PlaneGeometry(depth, height), sideMat));
      left.position.set(-width / 2, height / 2, 0);
      left.rotation.y = Math.PI / 2;

      var right = addPart(new THREE.Mesh(new THREE.PlaneGeometry(depth, height), sideMat));
      right.position.set(width / 2, height / 2, 0);
      right.rotation.y = -Math.PI / 2;

      var roof = addPart(new THREE.Mesh(
        /* Sized to the outside of the top plate, so no daylight shows in the
           gap between the schach and the frame. */
        new THREE.PlaneGeometry(width + POST, depth + POST),
        /* Unlit: we look at the schach from underneath, where a lit material
           only ever shows us the shadow side. The photo already has its light. */
        new THREE.MeshBasicMaterial({ map: tiled(TEXTURES.roof, width / 48, depth / 22), side: THREE.DoubleSide })
      ));
      roof.rotation.x = Math.PI / 2;
      roof.position.y = height + 7.4;   /* laid across the beams, not the walls */

      /* The timber. A real sukkah is a frame first: four corner posts, a 2×4
         plate running right round the top — the street side included, not just
         the two sides — and the cross beams that carry the schach resting on
         that plate. */
      var beamMat = new THREE.MeshLambertMaterial({ color: 0x8d6334 });
      var plateY = height + 1.75;

      function timber(sizeX, sizeY, sizeZ, x, y, z) {
        var piece = addPart(new THREE.Mesh(new THREE.BoxGeometry(sizeX, sizeY, sizeZ), beamMat));
        piece.position.set(x, y, z);
        return piece;
      }

      /* Top plate, all four sides. The corners overlap by the post width so
         there is no notch where two runs meet. */
      timber(width + POST, POST, POST, 0, plateY, -depth / 2);
      timber(width + POST, POST, POST, 0, plateY, depth / 2);
      timber(POST, POST, depth + POST, -width / 2, plateY, 0);
      timber(POST, POST, depth + POST, width / 2, plateY, 0);

      /* Corner posts, floor to plate. These are the poles the banners have to
         stop short of. */
      var postHeight = plateY - POST / 2;
      [[-1, -1], [1, -1], [1, 1], [-1, 1]].forEach(function (corner) {
        timber(POST, postHeight, POST,
          corner[0] * width / 2, postHeight / 2, corner[1] * depth / 2);
      });

      /* Cross beams over the top, spaced about every 40 inches. */
      var beamCount = Math.max(3, Math.round(width / 40));
      for (var i = 0; i <= beamCount; i += 1) {
        timber(POST, POST, depth, -width / 2 + (width * i) / beamCount, plateY + POST, 0);
      }
    }

    /* ---------------------------------------------------------- camera -- */

    /* A stand-inside-and-look-around camera, the way a property walkthrough
       works. Turning is unlimited, so every wall is reachable. The standpoint
       slides to the opposite side of the sukkah as you turn, which keeps the
       wall you are facing framed instead of pressed against your nose. */
    var look = { yaw: 0, pitch: 0, backoff: 200, targetYaw: null };
    var room3d = { width: 120, depth: 120, height: 96, eye: 48 };

    /* How far back to stand so the whole sukkah fits in the frame. The walls
       are single-sided and face inwards, so standing outside the box means the
       near wall simply is not drawn — you look straight into the room the way
       the reference photo does. Small sukkahs are viewed from further out
       rather than from a camera jammed against the inside of a wall. */
    function framingDistance() {
      var halfFov = (camera.fov * Math.PI / 180) / 2;
      var aspect = Math.max(0.6, camera.aspect);
      var acrossFit = (room3d.width / 2) / (aspect * Math.tan(halfFov));
      var heightFit = (room3d.height / 2) / Math.tan(halfFov);
      var depthFit = (room3d.depth / 2) / (aspect * Math.tan(halfFov));
      return Math.max(acrossFit, heightFit, depthFit) * 1.02;
    }

    /* How far up or down you may look: enough to take in the schach above and
       the floor below, never far enough to tip over the top or roll the
       horizon, which is what made the earlier free camera unusable. */
    var PITCH_LIMIT = 0.6;

    function applyCamera() {
      /* The camera circles the sukkah at one steady height. Turning goes all
         the way round; looking up and down swings the aim, not the standpoint,
         so the walls stay upright however far you tilt. */
      var backoff = Math.max(12, Math.min(look.backoff, framingDistance() * 3));
      camera.position.set(
        Math.sin(look.yaw) * backoff,
        room3d.eye,
        Math.cos(look.yaw) * backoff
      );
      camera.lookAt(0, room3d.eye + Math.tan(look.pitch) * backoff, 0);
      invalidate();
    }

    /* Which way to face to look straight at a given wall. */
    var WALL_YAW = [0, -Math.PI / 2, Math.PI, Math.PI / 2];

    var currentPlan = [0];

    function faceWall(index) {
      look.targetYaw = WALL_YAW[currentPlan[index] === undefined ? 0 : currentPlan[index]];
      look.backoff = framingDistance();
      invalidate();
    }

    /* One button per wall the shopper is decorating, so "look at wall 3" is a
       click rather than a hunt with the mouse. */
    var wallButtonHost = root.querySelector('[data-sukkah-wall-buttons]');
    var wallButtonKey = '';
    var SLOT_NAME = ['back', 'right', 'front', 'left'];

    /* Labelled by where each panel actually ended up, which depends on how many
       there are — with three panels the run is left, back, right. */
    function buildWallButtons(plan) {
      if (!wallButtonHost) return;
      var key = plan.join(',');
      if (key === wallButtonKey) return;
      wallButtonKey = key;
      wallButtonHost.innerHTML = '';
      if (plan.length < 2) return;
      plan.forEach(function (slot, index) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'sukkah-3d__wall-button';
        button.textContent = 'Panel ' + (index + 1) + ' \u00b7 ' + SLOT_NAME[slot];
        button.addEventListener('click', function () { faceWall(index); });
        wallButtonHost.appendChild(button);
      });
    }

    /* Turning and zooming by button, so the view is usable without anyone
       having to be good at dragging. Turns snap to whole walls. */
    var QUARTER = Math.PI / 2;

    function turnBy(step) {
      var base = look.targetYaw === null ? look.yaw : look.targetYaw;
      look.targetYaw = Math.round(base / QUARTER) * QUARTER + step * QUARTER;
      invalidate();
    }

    function zoomBy(step) {
      look.targetYaw = null;
      look.backoff *= step > 0 ? 0.8 : 1.25;
      applyCamera();
    }

    /* Look up towards the schach or down towards the floor, one step a click. */
    function tiltBy(step) {
      look.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, look.pitch + step * 0.2));
      applyCamera();
    }

    function stepTurn() {
      if (look.targetYaw === null) return false;
      var diff = look.targetYaw - look.yaw;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      if (Math.abs(diff) < 0.004) {
        look.yaw = look.targetYaw;
        look.targetYaw = null;
      } else {
        look.yaw += diff * 0.15;
      }
      applyCamera();
      return true;
    }

    var dragging = false;
    var lastX = 0;
    var lastY = 0;
    var pointers = {};
    var lastSpread = 0;

    canvasHost.addEventListener('pointerdown', function (event) {
      pointers[event.pointerId] = { x: event.clientX, y: event.clientY };
      dragging = true;
      lastX = event.clientX;
      lastY = event.clientY;
      canvasHost.setPointerCapture(event.pointerId);
      canvasHost.classList.add('is-dragging');
    });
    canvasHost.addEventListener('pointermove', function (event) {
      if (!dragging) return;
      if (pointers[event.pointerId]) {
        pointers[event.pointerId].x = event.clientX;
        pointers[event.pointerId].y = event.clientY;
      }
      var ids = Object.keys(pointers);
      if (ids.length >= 2) {
        var a = pointers[ids[0]];
        var b = pointers[ids[1]];
        var spread = Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y));
        if (lastSpread) {
          look.backoff *= lastSpread / Math.max(1, spread);
          applyCamera();
        }
        lastSpread = spread;
        return;
      }
      look.targetYaw = null;
      look.yaw += (event.clientX - lastX) * 0.0032;
      /* Drag down to look up, the way you would tilt a photograph towards you.
         Clamped, so the view can reach the schach and the floor and stop. */
      look.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT,
        look.pitch + (event.clientY - lastY) * 0.0026));
      lastX = event.clientX;
      lastY = event.clientY;
      applyCamera();
    });
    function endDrag(event) {
      delete pointers[event.pointerId];
      if (!Object.keys(pointers).length) {
        dragging = false;
        lastSpread = 0;
        canvasHost.classList.remove('is-dragging');
      }
    }
    canvasHost.addEventListener('pointerup', endDrag);
    canvasHost.addEventListener('pointercancel', endDrag);
    canvasHost.addEventListener('wheel', function (event) {
      event.preventDefault();
      look.targetYaw = null;
      look.backoff *= event.deltaY > 0 ? 1.09 : 0.91;
      applyCamera();
    }, { passive: false });

    /* ---------------------------------------------------------- artwork -- */

    var artImage = null;
    var artImageUrl = '';

    /* One texture per picture, uploaded to the graphics card once. Each banner
       gets a lightweight clone of it that carries its own offset and repeat —
       building a fresh THREE.Texture per banner per drag frame meant sending
       the whole photograph to the card dozens of times a second, which is what
       made dragging crawl. */
    var artTexture = null;

    function loadArtwork(url, done) {
      if (!url || url === artImageUrl) { done(); return; }
      var image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = function () {
        artImage = image;
        artImageUrl = url;
        if (artTexture) artTexture.dispose();
        artTexture = new THREE.Texture(artImage);
        artTexture.needsUpdate = true;
        artTexture.wrapS = THREE.ClampToEdgeWrapping;
        artTexture.wrapT = THREE.ClampToEdgeWrapping;
        artTexture.anisotropy = maxAnisotropy;
        done();
      };
      image.onerror = function () { done(); };
      image.src = url;
    }

    function coverScale(imageWidth, imageHeight, frameWidth, frameHeight) {
      return Math.max(frameWidth / imageWidth, frameHeight / imageHeight);
    }

    function coverOffset(renderedSize, frameSize, position) {
      if (renderedSize <= frameSize) return (frameSize - renderedSize) / 2;
      return -(renderedSize - frameSize) * Math.max(0, Math.min(1, position));
    }

    /* The slice of the artwork that belongs to one wall, expressed as texture
       offset and repeat. Mirrors the maths the drag box uses, so what hangs in
       here is what gets printed. */
    function sliceFor(view, wall) {
      if (!artImage || !artTexture) return null;
      var iw = artImage.naturalWidth;
      var ih = artImage.naturalHeight;
      var scale = coverScale(iw, ih, view.muralWidth, view.muralHeight);
      var renderedWidth = iw * scale;
      var renderedHeight = ih * scale;
      var positionX = view.mode === 'vertical' ? 0.5 : view.cropX;
      var positionY = view.mode === 'horizontal' ? 0.5 : view.cropY;
      var offsetX = coverOffset(renderedWidth, view.muralWidth, positionX);
      var offsetY = coverOffset(renderedHeight, view.muralHeight, positionY);

      /* from/to are fractions along this panel: a panel that runs past a
         corner is drawn as two pieces, each showing its own part. */
      var from = arguments.length > 2 ? arguments[2] : 0;
      var to = arguments.length > 3 ? arguments[3] : 1;
      var u0 = ((wall.left + wall.width * from - offsetX) / scale) / iw;
      var uw = ((wall.width * (to - from)) / scale) / iw;
      var yTop = ((0 - offsetY) / scale) / ih;
      var yBottom = ((wall.height - offsetY) / scale) / ih;
      /* Texture v runs bottom-up while the mural is measured from the top. */
      var vBottom = 1 - yBottom;
      var vHeight = yBottom - yTop;

      return { offsetU: u0, offsetV: vBottom, repeatU: uw, repeatV: vHeight };
    }

    /* Re-aim the existing banners at a new part of the picture. Nothing is
       rebuilt and nothing is uploaded — this is what runs while dragging. */
    function updateSlices(view) {
      signPieces.forEach(function (piece) {
        var wall = view.walls[piece.panel];
        if (!wall || !piece.mesh.material.map) return;
        var slice = sliceFor(view, wall, piece.from, piece.to);
        if (!slice) return;
        piece.mesh.material.map.offset.set(slice.offsetU, slice.offsetV);
        piece.mesh.material.map.repeat.set(slice.repeatU, slice.repeatV);
      });
    }

    var grommetGeometry = new THREE.CircleGeometry(0.55, 12);
    var grommetMaterial = new THREE.MeshBasicMaterial({ color: 0x8b8378 });
    var signParts = [];
    var signPieces = [];

    function clearSigns() {
      signPieces = [];
      signParts.forEach(function (part) {
        signs.remove(part);
        if (part.geometry && part.geometry !== grommetGeometry) part.geometry.dispose();
        if (part.material && part.material.map && part.material.map !== grommetMaterial.map) {
          part.material.map.dispose();
        }
      });
      signParts = [];
    }

    /* Where the panels sit in the sukkah.

       Slots are 0 back, 1 right, 2 front, 3 left. The picture always starts on
       the back wall — the middle wall you face when you walk in — and runs on
       clockwise from there, so panel 1 is in the same place however many
       panels were ordered:

         1 panel  → the back wall, straight ahead
         2 panels → back then right, seen together from that corner
         3 panels → back, right, front — all three in view, in order
         4 panels → back, right, front, left; the front one is behind you

       Each plan keeps the panels in consecutive slots going clockwise, so the
       picture still runs on around every corner. */
    var SLOT_PLAN = { 1: [0], 2: [0, 1], 3: [0, 1, 2], 4: [0, 1, 2, 3] };

    /* The angle that puts the ordered panels in the middle of the frame:
       the average of the directions of the walls they sit on. One panel faces
       it square on; two look into their shared corner; three read left to
       right across the frame; four leave the last one behind the camera. */
    function restingYaw(plan) {
      var x = 0;
      var z = 0;
      plan.forEach(function (slot) {
        x += Math.sin(WALL_YAW[slot]);
        z += Math.cos(WALL_YAW[slot]);
      });
      if (Math.abs(x) < 1e-6 && Math.abs(z) < 1e-6) return 0;
      return Math.atan2(x, z);
    }

    function slotPlan(count) {
      return SLOT_PLAN[Math.max(1, Math.min(4, count))] || SLOT_PLAN[1];
    }

    /* Each wall's banner starts at the corner where the previous one ended, so
       the mural runs on around the room. A banner narrower than the wall it is
       on leaves its bare strip at the far end, never split down both sides —
       centring it would break the picture at both corners. */
    function placements(width, depth) {
      return [
        { x: -width / 2, z: -depth / 2, dx: 1, dz: 0, rotation: 0 },            /* back  */
        { x: width / 2, z: -depth / 2, dx: 0, dz: 1, rotation: -Math.PI / 2 },  /* right */
        { x: width / 2, z: depth / 2, dx: -1, dz: 0, rotation: Math.PI },       /* front */
        { x: -width / 2, z: depth / 2, dx: 0, dz: -1, rotation: Math.PI / 2 }   /* left  */
      ];
    }

    function grommetPositions(mode, width, height) {
      var points = [];
      var inset = 1.5;
      var left = -width / 2 + inset;
      var right = width / 2 - inset;
      var top = height / 2 - inset;
      var bottom = -height / 2 + inset;
      var i;
      if (mode === 'corners') {
        points.push([left, top], [right, top], [left, bottom], [right, bottom]);
      } else if (mode === 'top' || mode === 'top-bottom' || mode === 'all-sides') {
        var across = Math.max(2, Math.ceil(width / 12));
        for (i = 0; i <= across; i += 1) {
          var x = left + ((right - left) * i) / across;
          points.push([x, top]);
          if (mode !== 'top') points.push([x, bottom]);
        }
        if (mode === 'all-sides') {
          var down = Math.max(1, Math.ceil(height / 12));
          for (i = 1; i < down; i += 1) {
            var y = bottom + ((top - bottom) * i) / down;
            points.push([left, y], [right, y]);
          }
        }
      }
      return points;
    }


    /* The panel number, painted onto the banner itself. The shopper reads it
       off the wall it belongs to, which is why the list of wall names under
       the view is no longer needed. */
    var panelLabelTextures = {};

    function panelLabelTexture(number) {
      if (panelLabelTextures[number]) return panelLabelTextures[number];
      var sheet = document.createElement('canvas');
      sheet.width = 512;
      sheet.height = 160;
      var context = sheet.getContext('2d');
      var radius = 26;
      context.fillStyle = 'rgba(24,18,12,.72)';
      context.beginPath();
      context.moveTo(radius, 0);
      context.arcTo(sheet.width, 0, sheet.width, sheet.height, radius);
      context.arcTo(sheet.width, sheet.height, 0, sheet.height, radius);
      context.arcTo(0, sheet.height, 0, 0, radius);
      context.arcTo(0, 0, sheet.width, 0, radius);
      context.closePath();
      context.fill();
      context.fillStyle = '#ffffff';
      context.font = '700 92px Arial, Helvetica, sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText('Panel ' + number, sheet.width / 2, sheet.height / 2 + 4);
      var texture = new THREE.CanvasTexture(sheet);
      texture.minFilter = THREE.LinearFilter;
      texture.anisotropy = maxAnisotropy;
      panelLabelTextures[number] = texture;
      return texture;
    }

    function addPanelLabel(mesh, wall, number) {
      /* Sized to the banner, capped so a huge wall does not get a huge label
         and a narrow one still gets a readable badge. */
      var height = Math.max(4, Math.min(wall.height * .14, 11));
      var width = Math.min(height * 3.2, wall.width * .8);
      height = width / 3.2;
      var label = new THREE.Mesh(
        new THREE.PlaneGeometry(width, height),
        new THREE.MeshBasicMaterial({ map: panelLabelTexture(number), transparent: true, depthWrite: false })
      );
      label.position.set(0, wall.height / 2 - height / 2 - height * .45, 0.25);
      mesh.add(label);
      signParts.push(label);
    }

    function buildSigns(view, roomWidth, roomDepth, roomHeight) {
      clearSigns();
      var spots = placements(roomWidth, roomDepth);
      var plan = slotPlan(view.walls.length);
      var topLine = roomHeight;   /* every banner hangs from the top of the wall */

      /* The signs are laid end to end around the room, measured as one running
         distance from the first corner rather than one sign per wall. So the
         run reads 1, 2, 3, 4 with no bare wood between them, and whatever the
         walls have left over lands in one piece after the last sign, before the
         picture starts again at panel 1 — which is where a shopper expects the
         join to be. A sign that meets a corner part way along is drawn as two
         pieces, one on each wall, so it turns the corner exactly as vinyl does.

         Walls run in the same clockwise order the signs do, and the whole
         perimeter is available even when fewer than four signs were ordered. */
      var perimeter = [];
      var walked = 0;
      for (var step = 0; step < 4; step += 1) {
        var slot = (plan[0] + step) % 4;
        var length = (slot === 0 || slot === 2) ? roomWidth : roomDepth;
        perimeter.push({ slot: slot, start: walked, length: length });
        walked += length;
      }

      var runAt = 0;
      view.walls.forEach(function (wall, index) {
        var runFrom = runAt;
        var runTo = runAt + wall.width;
        runAt = runTo;
        var widest = null;
        var widestSpan = 0;

        perimeter.forEach(function (leg) {
          var from = Math.max(runFrom, leg.start);
          var to = Math.min(runTo, leg.start + leg.length);
          var span = to - from;
          if (span < 0.5) return;   /* nothing of this sign lands on this wall */

          var slice = sliceFor(view, wall, (from - runFrom) / wall.width, (to - runFrom) / wall.width);
          var texture = null;
          if (slice) {
            texture = artTexture.clone();
            texture.needsUpdate = true;
            texture.offset.set(slice.offsetU, slice.offsetV);
            texture.repeat.set(slice.repeatU, slice.repeatV);
          }
          /* Unlit, so the artwork shows its true printed colours rather than
             whatever the scene lighting does to them. */
          var material = texture
            ? new THREE.MeshBasicMaterial({ map: texture })
            : new THREE.MeshBasicMaterial({ color: 0xb9ada0 });
          var mesh = new THREE.Mesh(new THREE.PlaneGeometry(span, wall.height), material);

          var spot = spots[leg.slot];
          var middle = (from + to) / 2 - leg.start;   /* along this wall */
          var lift = 0.6;   /* a hair off the wood so the two never z-fight */
          var x = spot.x + spot.dx * middle;
          var z = spot.z + spot.dz * middle;
          if (spot.dx === 0) x += spot.x > 0 ? -lift : lift;
          if (spot.dz === 0) z += spot.z > 0 ? -lift : lift;
          mesh.position.set(x, topLine - wall.height / 2, z);
          mesh.rotation.y = spot.rotation;
          signs.add(mesh);
          signParts.push(mesh);
          signPieces.push({ panel: index, mesh: mesh, from: (from - runFrom) / wall.width, to: (to - runFrom) / wall.width });

          if (span > widestSpan) {
            widestSpan = span;
            widest = { mesh: mesh, width: span };
          }

          /* Grommets belong to the sign, so they are spread along the whole of
             it and only the ones on this piece are drawn. */
          grommetPositions(view.grommetMode, wall.width, wall.height).forEach(function (point) {
            var alongSign = point[0] + wall.width / 2;          /* 0 … sign width */
            var alongPiece = alongSign - (from - runFrom);
            if (alongPiece < 0 || alongPiece > span) return;
            var dot = new THREE.Mesh(grommetGeometry, grommetMaterial);
            dot.position.set(alongPiece - span / 2, point[1], 0.06);
            mesh.add(dot);
            signParts.push(dot);
          });
        });

        /* The number goes on the larger half of a sign that turns a corner. */
        if (widest) addPanelLabel(widest.mesh, { width: widest.width, height: wall.height }, index + 1);
      });
    }

    /* ----------------------------------------------------------- frame -- */

    var needsRender = true;
    function invalidate() { needsRender = true; }
    function renderLoop() {
      requestAnimationFrame(renderLoop);
      stepTurn();
      if (!needsRender) return;
      needsRender = false;
      renderer.render(scene, camera);
    }

    var lastCanvasWidth = 0;
    var lastCanvasHeight = 0;
    function resize() {
      var width = canvasHost.clientWidth || 640;
      var height = canvasHost.clientHeight || 380;
      if (width === lastCanvasWidth && height === lastCanvasHeight) return;
      lastCanvasWidth = width;
      lastCanvasHeight = height;
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      invalidate();
    }
    var resizeFrame = 0;
    window.addEventListener('resize', function () {
      if (resizeFrame) return;
      resizeFrame = requestAnimationFrame(function () {
        resizeFrame = 0;
        resize();
      });
    });

    var lastRoomKey = '';
    var lastPlanKey = '';
    var lastShapeKey = '';
    var hasFramed = false;

    var lastView = null;
    function apply(view) {
      lastView = view;
      var plan = slotPlan(view.walls.length);
      currentPlan = plan;

      /* The sukkah takes its shape from the measurements: the walls on the
         back and front set how wide it is, the two sides set how deep. */
      var slots = [null, null, null, null];
      view.walls.forEach(function (wall, index) {
        slots[plan[index]] = wall;
      });
      var across = Math.max(slots[0] ? slots[0].width : 0, slots[2] ? slots[2].width : 0);
      var along = Math.max(slots[1] ? slots[1].width : 0, slots[3] ? slots[3].width : 0);
      /* Sides nobody measured take their size from the sides that were
         measured, so a single 16 ft wall gives a 16 ft sukkah rather than a
         long tunnel. The sukkah always follows the panels — there is nothing
         for the shopper to set here. */
      var chosen = view.fallbackWall > 0 ? view.fallbackWall : 0;
      var roomWidth = across || chosen || along || 120;
      var roomDepth = along || chosen || across || 120;
      roomWidth = Math.max(36, roomWidth);
      roomDepth = Math.max(36, roomDepth);
      var tallest = 1;
      view.walls.forEach(function (wall) { if (wall.height > tallest) tallest = wall.height; });
      /* The sukkah is the size the shopper measured. The sign is made to that
         wall, so on screen it covers that wall completely — no bare wood above
         or below it, and the room grows, shrinks, widens and narrows with the
         numbers they type. */
      var roomHeight = Math.max(24, tallest);

      /* Dragging the picture changes only which part of it each banner shows.
         Nothing about the room, the banners or the buttons has to be rebuilt
         for that, so take the cheap path: move the slices and redraw. */
      var shapeKey = [
        plan.join(','), roomWidth, roomDepth, roomHeight,
        view.walls.map(function (wall) { return wall.width + 'x' + wall.height + '@' + wall.left; }).join(','),
        view.grommetMode, view.imageUrl
      ].join('|');
      if (shapeKey === lastShapeKey && signPieces.length > 0 && artTexture) {
        updateSlices(view);
        invalidate();
        return;
      }
      lastShapeKey = shapeKey;

      var planKey = plan.join(',');
      var planChanged = planKey !== lastPlanKey;
      lastPlanKey = planKey;
      var roomKey = [roomWidth, roomDepth, roomHeight].join(':');
      root.setAttribute('data-room', roomKey);   /* handy when diagnosing */
      var roomChanged = roomKey !== lastRoomKey;
      if (roomChanged) {
        lastRoomKey = roomKey;
        buildRoom(roomWidth, roomDepth, roomHeight);
      }

      loadArtwork(view.imageUrl, function () {
        buildSigns(view, roomWidth, roomDepth, roomHeight);
        /* Aim at the back wall and stand back just far enough to take it in,
           without letting the camera step outside the sukkah. */
        room3d.width = roomWidth;
        room3d.depth = roomDepth;
        room3d.height = roomHeight;
        /* Look at the banners, not at the middle of the bare wall. */
        room3d.eye = roomHeight * 0.5;
        if (!hasFramed || roomChanged || planChanged) {
          /* First open, a new shape, or a different number of panels: aim at
             the middle of what was ordered and stand back far enough to take
             it all in. Nobody should have to drag to find their own signs. */
          hasFramed = true;
          look.yaw = restingYaw(plan);
          look.targetYaw = null;
          look.pitch = 0;
          look.backoff = framingDistance();
        }
        applyCamera();
        resize();
        invalidate();

      });
    }

    applyCamera();
    resize();
    renderLoop();

    return {
      apply: apply,
      resize: resize,
      resetView: function () {
        hasFramed = false;
        lastShapeKey = '';   /* force the full path so the camera reframes */
        apply(lastView);
      },
      faceWall: faceWall,
      turnBy: turnBy,
      tiltBy: tiltBy,
      zoomBy: zoomBy
    };
  }

  /* --------------------------------------------------------------- wiring -- */

  if (wallSelect) {
    wallSelect.addEventListener('change', function () {
      state.fallbackWall = parseFloat(wallSelect.value) || 0;
      if (viewer) viewer.apply(state);
    });
  }
  if (resetButton) {
    resetButton.addEventListener('click', function () {
      if (viewer) viewer.resetView();
    });
  }
  root.querySelectorAll('[data-sukkah-turn]').forEach(function (control) {
    control.addEventListener('click', function () {
      if (viewer) viewer.turnBy(parseFloat(control.getAttribute('data-sukkah-turn')) || 1);
    });
  });
  root.querySelectorAll('[data-sukkah-tilt]').forEach(function (control) {
    control.addEventListener('click', function () {
      if (viewer) viewer.tiltBy(parseFloat(control.getAttribute('data-sukkah-tilt')) || 1);
    });
  });
  root.querySelectorAll('[data-sukkah-zoom]').forEach(function (control) {
    control.addEventListener('click', function () {
      if (viewer) viewer.zoomBy(parseFloat(control.getAttribute('data-sukkah-zoom')) || 1);
    });
  });

  document.addEventListener('sign:change', function (event) {
    var detail = event.detail || {};
    if (detail.walls && detail.walls.length) state.walls = detail.walls;
    if (detail.muralWidth) state.muralWidth = detail.muralWidth;
    if (detail.muralHeight) state.muralHeight = detail.muralHeight;
    if (typeof detail.cropX === 'number') state.cropX = detail.cropX;
    if (typeof detail.cropY === 'number') state.cropY = detail.cropY;
    if (detail.mode) state.mode = detail.mode;
    if (detail.grommetMode) state.grommetMode = detail.grommetMode;
    /* The customizer points at the full print-resolution file. For a wall in a
       3D room a smaller copy looks identical and opens far faster, so only take
       the customizer's URL when it is the shopper's own uploaded artwork. */
    if (detail.imageUrl && /^(blob:|data:)/.test(detail.imageUrl)) state.imageUrl = detail.imageUrl;
    if (viewer) viewer.apply(state);
  });
})();
